import React, { useState } from "react";
import {
  DatePicker,
  Form,
  Switch,
  Tooltip,
  Input,
  AutoComplete,
  InputNumber,
  FormInstance,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled, { css } from "styled-components";
import CustomComponents from "./CustomComponents";
import SingleSelectComponent from "Components/CommonComponents/SingleSelectComponent";
import { sanSub, sadSub, satSub } from "utilFunctions/Constants/Constants";
import MutlSelectAntdComponent from "utilFunctions/shared/AntdSharedComponents/MutlSelectAntdComponent";

const { RangePicker } = DatePicker;

/* ---------------------------------- */
/* Types */
/* ---------------------------------- */

interface DynamicField {
  name: string;
  label?: React.ReactNode;
  componentType: string;
  placeholder?: string;
  tooltip?: string;
  rules?: any[];
  dependencies?: string[];
  options?: any[];
  disabled?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: string;
  min?: number;
  layout?: "horizontal" | "vertical" | "inline";
  customType?: string;
  styleProps?: Record<string, any>;
  componentProps?: Record<string, any>;
  inputRef?: React.Ref<any>;
  ref?: React.Ref<any>;
  onChange?: (...args: any[]) => void;
  maxTagCount?: number;
}

interface DynamicFormItemProps {
  field: DynamicField;
  form: FormInstance;
}

/* ---------------------------------- */
/* Constants */
/* ---------------------------------- */

const shortType: Record<string, any[]> = {
  SAN: sanSub,
  SAD: sadSub,
  SAT: satSub,
};

/* ---------------------------------- */
/* Styled Components */
/* ---------------------------------- */

const noBorderStyle = css`
  border: none !important;
  box-shadow: none !important;
`;

export const CustomInput = styled(Input)<{
  noBorder?: boolean;
  background?: string;
}>`
  &.ant-input {
    border: ${({ noBorder }) => (noBorder ? "none" : "")} !important;
    background: ${({ background }) => background || ""} !important;
  }

  &:hover,
  &:focus {
    border: ${({ noBorder }) => (noBorder ? "none" : "")} !important;
    box-shadow: none;
  }
`;

export const CustomInputNumber = styled(InputNumber)<{
  $noBorder?: boolean;
}>`
  width: 100%;
  ${({ $noBorder }) => $noBorder && noBorderStyle}
`;

export const CustomAutoComplete = styled(AutoComplete)<{
  $noBorder?: boolean;
}>`
  ${({ $noBorder }) =>
    $noBorder &&
    css`
      input {
        ${noBorderStyle}
      }
    `}
`;

export const CustomSwitch = styled(Switch)<{
  $noBorder?: boolean;
}>`
  ${({ $noBorder }) =>
    $noBorder &&
    css`
      &.ant-switch {
        border: none !important;
        box-shadow: none !important;
      }
    `}
`;

export const CustomRangePicker = styled(RangePicker)<{
  $noBorder?: boolean;
}>`
  ${({ $noBorder }) => $noBorder && noBorderStyle}
`;

export const CustomDatePicker = styled(DatePicker)<{
  $noBorder?: boolean;
}>`
  width: 100%;
  ${({ $noBorder }) => $noBorder && noBorderStyle}
`;

/* ---------------------------------- */
/* Component */
/* ---------------------------------- */

const DynamicFormItem: React.FC<DynamicFormItemProps> = ({
  field,
  form,
}: any) => {
  const [isDisabled] = useState<boolean>(true);
  const questionTypeChange = Form.useWatch("questionType", form);

  const renderSuffix = (tooltip?: string) =>
    tooltip ? (
      <Tooltip title={tooltip}>
        <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
      </Tooltip>
    ) : null;

  const renderFieldComponent = () => {
    const commonProps: any = {
      placeholder: field.placeholder,
      allowClear: field.allowClear,
      options: field.options,
      disabled: field.disabled,
      ...field,
      ...field.styleProps,
      ...field.componentProps,
      form,
    };
    const CustomComponent: any = CustomComponents;
    const returnOptions = () => {
      if (field.name === "subQuestionType") {
        return isDisabled ? field.options : shortType[questionTypeChange];
      }
      return field.options;
    };

    switch (field.componentType) {
      case "select":
        return (
          <SingleSelectComponent
            {...commonProps}
            mode={field.mode}
            showSearch={field.showSearch}
            onChange={field.onChange}
            options={returnOptions()}
            disabled={field.disabled}
            ref={field.ref}
          />
        );

      case "multi-select":
        return (
          <MutlSelectAntdComponent
            {...commonProps}
            mode="multiple"
            name={field.name}
            maxTagCount={field.maxTagCount || 2}
            showSearch={field.showSearch}
            onChangeHandler={field.onChange}
          />
        );

      case "input":
        return (
          <CustomInput {...commonProps} suffix={renderSuffix(field.tooltip)} />
        );

      case "input-number":
        return (
          <CustomInputNumber
            {...commonProps}
            min={field.min}
            ref={field.inputRef}
            suffix={renderSuffix(field.tooltip)}
          />
        );

      case "autocomplete":
        return <CustomAutoComplete {...commonProps} />;

      case "switch":
        return <CustomSwitch {...commonProps} />;

      case "date":
        return <CustomRangePicker {...commonProps} />;

      case "datePicker":
        return <CustomDatePicker {...commonProps} />;

      case "custom":
        return CustomComponent[field.customType!]?.({ field, form });

      default:
        return null;
    }
  };

  return (
    <Form.Item
      name={field.name}
      label={field.label}
      rules={field.rules}
      dependencies={field.dependencies}
      layout={field.layout || "vertical"}
    >
      {renderFieldComponent()}
    </Form.Item>
  );
};

export default DynamicFormItem;
